import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Check, CalendarClock, MessageCircle, UserX, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, today, useCan } from "@/lib/api";
import { Empty, StatusBadge } from "@/components/common";
import AppointmentForm from "@/components/forms/AppointmentForm";

export default function AppointmentList({ rows, onChanged, hidePatient }) {
  const can = useCan();
  const [edit, setEdit] = useState(null);
  const now = today();
  const setStatus = async (a, status) => {
    try { await api.patch(`/appointments/${a.id}`, { status }); toast.success(`Marked ${status}`); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remind = async (a) => {
    const w = window.open("", "_blank");
    try {
      const r = await api.get(`/appointments/${a.id}/reminder`);
      if (!r.data.whatsapp_url) { w?.close(); return toast.error("Add the patient's phone number first"); }
      if (w) w.location.href = r.data.whatsapp_url; else window.location.href = r.data.whatsapp_url;
      await api.post(`/appointments/${a.id}/reminded`); onChanged();
    } catch (e) { w?.close(); toast.error(errMsg(e)); }
  };
  if (!rows.length) return <Empty icon={CalendarDays} title="No appointments here" text="Book a follow-up to see it in this list." testid="appointments-empty" />;
  return (
    <div className="appt-list">
      {rows.map((a) => {
        const overdue = a.status === "scheduled" && a.date < now;
        return (
          <div className={`appt-row ${overdue ? "overdue" : ""}`} key={a.id} data-testid={`appointment-row-${a.id}`}>
            <div className="appt-when"><b>{new Date(`${a.date}T00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</b><small>{a.time || "Any time"}</small></div>
            <div className="appt-body">
              {!hidePatient && <Link to={`/patients/${a.patient_id}`} className="patient-link" data-testid={`appointment-patient-${a.id}`}>{a.patient_name}<small>{a.opd}{a.phone && ` · ${a.phone}`}</small></Link>}
              <span>{a.purpose}</span>{a.notes && <small className="sub">{a.notes}</small>}
              {a.reminded_at && <small className="sub reminded" data-testid={`appointment-reminded-${a.id}`}>Reminder sent {new Date(a.reminded_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}{a.reminded_by && ` by ${a.reminded_by}`}</small>}
            </div>
            <StatusBadge value={overdue ? "overdue" : a.status} testid={`appointment-status-${a.id}`} />
            <div className="row-actions">
              {a.status === "scheduled" && a.date >= now && <button className={a.reminded_at ? "wa-btn sent" : "wa-btn"} onClick={() => remind(a)} data-testid={`appointment-remind-${a.id}`}><MessageCircle size={14} /> {a.reminded_at ? "Resend" : "WhatsApp"}</button>}
            </div>
            {can("appointments:write") && <div className="row-actions">
              {a.status !== "completed" && <button className="icon-btn" title="Mark attended" onClick={() => setStatus(a, "completed")} data-testid={`appointment-complete-${a.id}`}><Check size={15} /></button>}
              {a.status === "scheduled" && <button className="icon-btn" title="Mark missed" onClick={() => setStatus(a, "missed")} data-testid={`appointment-missed-${a.id}`}><UserX size={15} /></button>}
              <button className="icon-btn" title="Reschedule" onClick={() => setEdit(a)} data-testid={`appointment-reschedule-${a.id}`}><CalendarClock size={15} /></button>
              {a.status === "scheduled" && <button className="icon-btn" title="Cancel" onClick={() => setStatus(a, "cancelled")} data-testid={`appointment-cancel-${a.id}`}><X size={15} /></button>}
            </div>}
          </div>
        );
      })}
      {edit && <AppointmentForm appointment={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); onChanged(); }} />}
    </div>
  );
}
